import React, { useState, useEffect, useCallback, useRef } from "react";
import { apiRequest } from "../utils/api";
import { Plus, CheckCircle, XCircle, Save, AlertTriangle, Search, MoreHorizontal, ArrowRightLeft, Trash2, AlertCircle } from "lucide-react";
import { Button, Toggle, PageHeader, EmptyState, IllustrationNoSelection, Spinner, Input } from "./ui";

const CACHE_NEW_KEY = "promotions:new";

function readCache()       { try { const r = localStorage.getItem(CACHE_NEW_KEY); return r ? JSON.parse(r) : null; } catch { return null; } }
function writeCache(value) { try { localStorage.setItem(CACHE_NEW_KEY, JSON.stringify(value)); } catch {} }
function clearCache()      { try { localStorage.removeItem(CACHE_NEW_KEY); } catch {} }

const normalizeCode = (code) => (code || "").trim().toUpperCase();

// The API returns 409 when a source code is already active elsewhere.
// ASSUMPTION: apiRequest throws on non-2xx and exposes either err.status or
// the API's error message. Adjust this check to match utils/api.js.
function isDuplicateError(err) {
  return err?.status === 409 || /already active/i.test(err?.message || "");
}

// ── Pulse highlight style ─────────────────────────────────────────────────────
const PULSE_STYLE = `
  @keyframes scHighlight {
    0%   { background-color: var(--color-warning-bg); }
    60%  { background-color: var(--color-warning-bg); }
    100% { background-color: transparent; }
  }
  .sc-pulse { animation: scHighlight 1.4s ease-out forwards; }
`;

// ── Row action menu (⋯) ───────────────────────────────────────────────────────
function RowActionMenu({ row, onMove, onDelete }) {
  const [open, setOpen] = useState(false);
  const menuRef         = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={menuRef} style={{ position: "relative", display: "inline-block" }}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Row actions"
        className={[
          "inline-flex items-center justify-center w-7 h-7 rounded-[var(--radius-md)] border transition-colors",
          open
            ? "border-[var(--color-orange)] bg-[var(--color-orange-light)] text-[var(--color-orange)]"
            : "border-[var(--color-border)] bg-transparent text-[var(--color-text-muted)] hover:border-[var(--color-orange)] hover:text-[var(--color-orange)]",
        ].join(" ")}
      >
        <MoreHorizontal size={13} />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-36 bg-white border border-[var(--color-border)] rounded-[var(--radius-lg)] shadow-md overflow-hidden">
          <button
            onClick={() => { setOpen(false); onMove(row); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-surface)] transition-colors"
          >
            <ArrowRightLeft size={13} className="text-[var(--color-text-muted)] shrink-0" />
            Move
          </button>
          <div className="h-px bg-[var(--color-border)] mx-2" />
          <button
            onClick={() => { setOpen(false); onDelete(row); }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-[var(--color-error)] hover:bg-[var(--color-error-bg)] transition-colors"
          >
            <Trash2 size={13} className="shrink-0" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

// ── Move modal ────────────────────────────────────────────────────────────────
function MoveModal({ row, promotions, currentPromotionId, onConfirm, onCancel, saving }) {
  const [search,   setSearch]   = useState("");
  const [selected, setSelected] = useState(null);
  const inputRef                = useRef(null);

  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, []);

  const filtered = promotions
    .filter(p => p.PromotionID !== currentPromotionId)
    .filter(p => !search || p.PromotionName.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 5);

  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-6">
      <div className="bg-white rounded-[var(--radius-xl)] shadow-xl w-full max-w-sm overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b border-[var(--color-border)]">
          <p className="text-sm font-bold text-[var(--color-text-primary)] mb-1">Move source code</p>
          <p className="text-xs text-[var(--color-text-muted)]">
            <span className="font-mono bg-[var(--color-surface-alt)] px-1.5 py-0.5 rounded">{row.SourceCode}</span>
            {" "}→ select a new promotion
          </p>
        </div>

        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] pointer-events-none" />
            <input
              ref={inputRef}
              value={search}
              onChange={e => { setSearch(e.target.value); setSelected(null); }}
              placeholder="Search promotions..."
              className="w-full h-9 pl-8 pr-3 rounded-[var(--radius-lg)] border border-[var(--color-border)] text-xs text-[var(--color-text-primary)] focus:outline-none focus:border-[var(--color-orange)] focus:ring-1 focus:ring-[var(--color-orange)]"
            />
          </div>
        </div>

        <div className="px-2 pb-2 min-h-10">
          {filtered.length === 0 ? (
            <p className="text-xs text-[var(--color-text-muted)] px-2 py-2">No promotions found.</p>
          ) : filtered.map(p => (
            <button
              key={p.PromotionID}
              onClick={() => setSelected(p)}
              className={[
                "w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-[var(--radius-md)] mb-0.5 border text-left transition-colors",
                selected?.PromotionID === p.PromotionID
                  ? "border-[var(--color-orange)] bg-[var(--color-orange-light)]"
                  : "border-transparent hover:bg-[var(--color-surface)]",
              ].join(" ")}
            >
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-semibold truncate ${selected?.PromotionID === p.PromotionID ? "text-[var(--color-orange)]" : "text-[var(--color-text-primary)]"}`}>
                  {p.PromotionName}
                </p>
                {p.DateLaunched && <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5">{p.DateLaunched}</p>}
              </div>
              <span className={`w-2 h-2 rounded-full shrink-0 ${p.IsActive !== false ? "bg-[var(--color-success)]" : "bg-[var(--color-border-strong)]"}`} />
            </button>
          ))}
        </div>

        <div className="px-4 pb-4 pt-3 border-t border-[var(--color-border)] flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button
            variant="primary"
            size="sm"
            onClick={() => selected && onConfirm(selected)}
            disabled={!selected || saving}
            loading={saving}
          >
            {saving ? "Moving…" : "Move"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Delete confirmation modal ─────────────────────────────────────────────────
function DeleteModal({ row, onConfirm, onCancel, saving }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-6">
      <div className="bg-white rounded-[var(--radius-xl)] shadow-xl w-full max-w-xs overflow-hidden">
        <div className="px-5 py-5">
          <p className="text-sm font-bold text-[var(--color-text-primary)] mb-2">Delete source code?</p>
          <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
            <span className="font-mono bg-[var(--color-surface-alt)] px-1.5 py-0.5 rounded">{row.SourceCode}</span>
            {" "}will be removed from this promotion. Transactions using this code will stop showing this promotion in the tracker.
          </p>
        </div>
        <div className="px-4 pb-4 pt-2 border-t border-[var(--color-border)] flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button>
          <Button variant="danger" size="sm" onClick={onConfirm} disabled={saving} loading={saving}>
            {saving ? "Deleting…" : "Delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Duplicate warning modal ───────────────────────────────────────────────────
function DuplicateWarningModal({ conflicts, addCount, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/40 flex items-center justify-center p-6">
      <div className="bg-white rounded-[var(--radius-xl)] shadow-xl w-full max-w-sm overflow-hidden">
        <div className="px-5 py-5">
          <p className="flex items-center gap-2 text-sm font-bold text-[var(--color-text-primary)] mb-3">
            <AlertTriangle size={14} className="text-[var(--color-warning)] shrink-0" />
            {conflicts.length} source code{conflicts.length === 1 ? " is" : "s are"} already in use
          </p>
          <ul className="text-xs text-[var(--color-text-secondary)] space-y-1.5 mb-3 max-h-40 overflow-y-auto">
            {conflicts.map((c, i) => (
              <li key={`${c.SourceCode}-${i}`}>
                <span className="font-mono bg-[var(--color-surface-alt)] px-1.5 py-0.5 rounded">{c.SourceCode}</span>
                {" "}{c.where}
              </li>
            ))}
          </ul>
          <p className="text-xs text-[var(--color-text-secondary)] leading-relaxed">
            {addCount > 0
              ? `Continue? The other ${addCount} will be added and these will be skipped.`
              : "There's nothing new to add. Continuing saves any other changes and skips these."}
          </p>
        </div>
        <div className="px-4 pb-4 pt-2 border-t border-[var(--color-border)] flex gap-2 justify-end">
          <Button variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={onConfirm}>
            {addCount > 0 ? `Add ${addCount}, skip ${conflicts.length}` : "Continue"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Source codes table ────────────────────────────────────────────────────────
function SourceCodesTable({ rows, onRowChange, onAddRow, onMoveRow, onDeleteRow, highlightRowId, searchQuery, onSearchChange }) {
  const [editingCell, setEditingCell] = useState(null);
  const [showWarning, setShowWarning] = useState(false);
  const rowRefs = useRef({});

  // Trigger pulse on the highlighted row after render
  useEffect(() => {
    if (!highlightRowId) return;
    const el = rowRefs.current[highlightRowId];
    if (!el) return;
    el.classList.remove("sc-pulse");
    void el.offsetWidth; // force reflow to restart animation
    el.classList.add("sc-pulse");
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightRowId]);

  const handleCellFocus = (rowIdx, field) => {
    setEditingCell({ rowIdx, field });
    if (field === "SourceCode") setShowWarning(true);
  };

  const handleCellBlur = (rowIdx, field, value) => {
    setEditingCell(null);
    onRowChange(rowIdx, field, value);
  };

  const handlePaste = (e, rowIdx) => {
    const text = e.clipboardData.getData("text");
    if (!text.includes("\t") && !text.includes("\n")) return;
    e.preventDefault();
    const parsed = text.split("\n").map(l => l.trimEnd()).filter(Boolean).map(line => {
      const parts = line.split("\t");
      return { _localId: `new_${Date.now()}_${Math.random()}`, _isNew: true, SourceCode: normalizeCode(parts[0]), SourceCodeDescription: (parts[1] || "").trim() };
    });
    onRowChange(rowIdx, "_paste", parsed);
  };

  // Filter rows by search query within the detail panel
  const term = (searchQuery || "").toLowerCase();
  const filteredRows = term
    ? rows.filter(r =>
        r.SourceCode?.toLowerCase().includes(term) ||
        r.SourceCodeDescription?.toLowerCase().includes(term)
      )
    : rows;

  const thClass = "px-3 py-2.5 text-left text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider border-b border-[var(--color-border)]";
  const tdClass = "px-1.5 py-1";
  const cellInput = (focused) => [
    "w-full px-2 py-1.5 rounded-[var(--radius-sm)] text-xs text-[var(--color-text-primary)] border transition-colors focus:outline-none",
    focused ? "border-[var(--color-orange)] bg-[var(--color-orange-light)]" : "border-transparent bg-transparent hover:border-[var(--color-border)]",
  ].join(" ");

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">Source Codes</p>
        <Button variant="secondary" size="sm" onClick={onAddRow} icon={<Plus size={12} />}>
          Add Row
        </Button>
      </div>

      {showWarning && (
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-[var(--radius-md)] bg-[var(--color-warning-bg)] border border-[var(--color-warning-border)] mb-3 text-xs text-[var(--color-warning)]">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" />
          <span>Editing the <strong>Source Code</strong> string affects fulfillment matching. Make sure the new value matches exactly what appears in BBCRM.</span>
        </div>
      )}

      {/* Source code search within detail panel */}
      <div className="relative mb-3">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] pointer-events-none" />
        <input
          value={searchQuery || ""}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Filter source codes…"
          className="w-full pl-8 pr-3 h-8 rounded-[var(--radius-md)] border border-[var(--color-border)] text-xs text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] bg-white focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)]"
        />
      </div>

      <div className="overflow-x-auto rounded-[var(--radius-lg)] border border-[var(--color-border)]">
        <table className="w-full border-collapse text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: "150px" }} />
            <col />
            <col style={{ width: "44px" }} />
          </colgroup>
          <thead className="bg-[var(--color-surface)]">
            <tr>
              <th className={thClass}>Source Code</th>
              <th className={thClass}>Description</th>
              <th className={`${thClass} text-center`} />
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 && (
              <tr>
                <td colSpan={3} className="py-6 text-center text-xs text-[var(--color-text-muted)]">
                  {term ? "No source codes match your search." : 'No source codes yet. Click "Add Row" or paste rows from Excel.'}
                </td>
              </tr>
            )}
            {filteredRows.map((row, i) => {
              // Index into the FULL rows list, not the filtered one, so edits
              // land on the right row while a filter is applied.
              const idx = rows.indexOf(row);
              return (
                <tr
                  key={row._localId || row.RowID || i}
                  ref={el => { if (row.RowID) rowRefs.current[row.RowID] = el; }}
                  className="border-t border-[var(--color-border)]"
                >
                  <td className={tdClass}>
                    <input
                      defaultValue={row.SourceCode || ""}
                      onFocus={() => handleCellFocus(idx, "SourceCode")}
                      onBlur={e => handleCellBlur(idx, "SourceCode", e.target.value)}
                      onPaste={e => handlePaste(e, idx)}
                      className={cellInput(editingCell?.rowIdx === idx && editingCell?.field === "SourceCode")}
                      placeholder="e.g. 10759A"
                      spellCheck={false}
                    />
                  </td>
                  <td className={tdClass}>
                    <input
                      defaultValue={row.SourceCodeDescription || ""}
                      onFocus={() => handleCellFocus(idx, "SourceCodeDescription")}
                      onBlur={e => handleCellBlur(idx, "SourceCodeDescription", e.target.value)}
                      onPaste={e => handlePaste(e, idx)}
                      className={cellInput(editingCell?.rowIdx === idx && editingCell?.field === "SourceCodeDescription")}
                      placeholder="Human-readable description"
                    />
                  </td>
                  <td className={`${tdClass} text-center`}>
                    {row.RowID
                      ? <RowActionMenu row={row} onMove={onMoveRow} onDelete={onDeleteRow} />
                      : <div className="w-7 h-7" />
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Promotion detail form ─────────────────────────────────────────────────────
function PromotionForm({ promotion, sourceCodes, promotions, onSourceCodesChange, onChange, onSave, onToggleSave, saving, saveStatus, saveMessage, allPromotionNames, onReloadSourceCodes, highlightRowId, scSearch, onScSearchChange }) {
  const isNew = !promotion.PromotionID;

  const [moveRow,      setMoveRow]      = useState(null);
  const [deleteRow,    setDeleteRow]    = useState(null);
  const [actionSaving, setActionSaving] = useState(false);

  const handleMoveConfirm = async (targetPromo) => {
    setActionSaving(true);
    try {
      await apiRequest(`/promotions/${promotion.PromotionID}/source-codes/${moveRow.RowID}`, {
        method: "PATCH",
        body: JSON.stringify({ PromotionID: targetPromo.PromotionID }),
      });
      setMoveRow(null);
      await onReloadSourceCodes();
    } catch (err) {
      console.error("Move failed", err);
    } finally {
      setActionSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setActionSaving(true);
    try {
      await apiRequest(`/promotions/${promotion.PromotionID}/source-codes/${deleteRow.RowID}`, {
        method: "PATCH",
        body: JSON.stringify({ isActiveSourceCode: false }),
      });
      setDeleteRow(null);
      await onReloadSourceCodes();
    } catch (err) {
      console.error("Delete failed", err);
    } finally {
      setActionSaving(false);
    }
  };

  const nameError =
    (promotion.PromotionName || "").trim() &&
    allPromotionNames
      .filter(n => String(n.id) !== String(promotion.PromotionID))
      .some(n => n.name.toLowerCase() === (promotion.PromotionName || "").trim().toLowerCase())
      ? "A promotion with this name already exists."
      : null;

  const canSave = (promotion.PromotionName || "").trim() && (promotion.DateLaunched || "").trim() && !nameError;

  const handleAddRow = () => {
    onSourceCodesChange([{ _localId: `new_${Date.now()}`, _isNew: true, SourceCode: "", SourceCodeDescription: "", PromotionID: promotion.PromotionID || null }, ...sourceCodes]);
  };

  const handleRowChange = (idx, field, value) => {
    if (idx < 0) return;
    if (field === "_paste") {
      const current = sourceCodes[idx];
      const drop    = current && (current._isNew || !current.RowID) && !current.SourceCode?.trim();
      onSourceCodesChange([...sourceCodes.slice(0, idx), ...(drop ? [] : [current]), ...value, ...sourceCodes.slice(idx + 1)]);
    } else {
      onSourceCodesChange(sourceCodes.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    }
  };

  const labelClass = "block text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider mb-1.5";
  const inputClass = "w-full h-9 px-3 rounded-[var(--radius-lg)] border text-sm text-[var(--color-text-primary)] bg-white focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)] transition-colors";

  return (
    <>
      <div className="p-6 sm:p-8 overflow-y-auto h-full flex flex-col gap-6">

        <div>
          <h2 className="text-base font-semibold text-[var(--color-text-primary)] tracking-[-0.01em]">
            {isNew ? "New Promotion" : promotion.PromotionName || "Edit Promotion"}
          </h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            {isNew ? "Fill in the details below and save" : "Edit promotion details and source codes"}
          </p>
        </div>

        <div className="border-t border-[var(--color-border)]" />

        <div className="flex flex-col gap-4">
          <div>
            <label className={labelClass}>Promotion Name</label>
            <input
              value={promotion.PromotionName || ""}
              onChange={e => onChange("PromotionName", e.target.value)}
              placeholder="e.g. Spring 2026 Graduation"
              className={`${inputClass} ${nameError ? "border-[var(--color-error)]" : "border-[var(--color-border)]"}`}
            />
            {nameError && <p className="text-xs text-[var(--color-error)] mt-1">{nameError}</p>}
          </div>

          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className={labelClass}>Date Launched</label>
              <input
                type="date"
                value={promotion.DateLaunched || ""}
                onChange={e => onChange("DateLaunched", e.target.value)}
                className={`${inputClass} w-44 border-[var(--color-border)]`}
              />
            </div>
            <div>
              <label className={labelClass}>Active</label>
              <Toggle
                checked={promotion.IsActive !== false}
                onChange={val => { onChange("IsActive", val); if (!isNew) onToggleSave("IsActive", val); }}
                label={promotion.IsActive !== false ? "Active" : "Inactive"}
              />
            </div>
            <div>
              <label className={labelClass}>Membership Promotion</label>
              <Toggle
                checked={promotion.IsMembership === true}
                onChange={val => { onChange("IsMembership", val); if (!isNew) onToggleSave("IsMembership", val); }}
                label={promotion.IsMembership ? "Membership" : "Non-Membership"}
              />
            </div>
          </div>
        </div>

        <div className="border-t border-[var(--color-border)]" />

        <SourceCodesTable
          rows={sourceCodes}
          onRowChange={handleRowChange}
          onAddRow={handleAddRow}
          onMoveRow={setMoveRow}
          onDeleteRow={setDeleteRow}
          highlightRowId={highlightRowId}
          searchQuery={scSearch}
          onSearchChange={onScSearchChange}
        />

        <div className="flex items-center gap-3 pb-2 flex-wrap">
          <Button
            variant="primary"
            size="md"
            onClick={() => canSave && onSave()}
            disabled={!canSave || saving}
            loading={saving}
            icon={!saving ? <Save size={14} /> : null}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
          {saveStatus === "saved" && (
            <span className="flex items-center gap-1.5 text-xs text-[var(--color-success)] font-medium">
              <CheckCircle size={13} /> {saveMessage || "Saved"}
            </span>
          )}
          {saveStatus === "error" && (
            <span className="flex items-center gap-1.5 text-xs text-[var(--color-error)] font-medium">
              <XCircle size={13} /> {saveMessage || "Save failed — try again"}
            </span>
          )}
        </div>
      </div>

      {moveRow && (
        <MoveModal
          row={moveRow}
          promotions={promotions}
          currentPromotionId={promotion.PromotionID}
          onConfirm={handleMoveConfirm}
          onCancel={() => setMoveRow(null)}
          saving={actionSaving}
        />
      )}

      {deleteRow && (
        <DeleteModal
          row={deleteRow}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteRow(null)}
          saving={actionSaving}
        />
      )}
    </>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function PromotionsManager() {
  const [promotions,     setPromotions]     = useState([]);
  const [allSourceCodes, setAllSourceCodes] = useState([]); // for cross-promotion search
  const [loading,        setLoading]        = useState(true);
  const [selectedPromo,  setSelectedPromo]  = useState(null);
  const [formData,       setFormData]       = useState({});
  const [sourceCodes,    setSourceCodes]    = useState([]);
  const [saving,         setSaving]         = useState(false);
  const [saveStatus,     setSaveStatus]     = useState(null);
  const [saveMessage,    setSaveMessage]    = useState(null);
  const [search,         setSearch]         = useState("");
  const [filterActive,   setFilterActive]   = useState("active");
  const [highlightRowId, setHighlightRowId] = useState(null);
  const [scSearch,       setScSearch]       = useState(""); // source code search in detail panel
  const [dupWarning,     setDupWarning]     = useState(null); // { conflicts, addCount }
  const saveStatusTimer                     = useRef(null);

  const loadPromotions = useCallback(async () => {
    setLoading(true);
    try {
      const [promoData, scData] = await Promise.all([
        apiRequest("/promotions"),
        apiRequest("/promotions/source-codes"),
      ]);
      setPromotions(Array.isArray(promoData) ? promoData : []);
      setAllSourceCodes(Array.isArray(scData) ? scData : []);
    } catch (err) {
      console.error("Failed to load promotions", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPromotions(); }, [loadPromotions]);

  // Cache new-promotion form state only
  useEffect(() => {
    if (selectedPromo !== "new") return;
    const hasContent = sourceCodes.length > 0 || Object.keys(formData).length > 0;
    if (hasContent) writeCache({ formData, sourceCodes });
  }, [formData, sourceCodes, selectedPromo]);

  const selectPromotion = useCallback(async (promo, highlightRowIdOverride = null) => {
    setSaveStatus(null);
    setScSearch("");
    try {
      const data = await apiRequest(`/promotions/${promo.PromotionID}`);
      setFormData({
        PromotionID:   data.PromotionID,
        PromotionName: data.PromotionName,
        DateLaunched:  data.DateLaunched || "",
        IsActive:      data.IsActive !== false,
        IsMembership:  data.IsMembership === true,
      });
      setSourceCodes(data.SourceCodes || []);
      setSelectedPromo(promo);
      if (highlightRowIdOverride) {
        setHighlightRowId(null);
        setTimeout(() => setHighlightRowId(highlightRowIdOverride), 100);
      } else {
        setHighlightRowId(null);
      }
    } catch (err) {
      console.error("Failed to fetch promotion", err);
    }
  }, []);

  const reloadSourceCodes = useCallback(async () => {
    if (!selectedPromo || selectedPromo === "new") return;
    try {
      const data = await apiRequest(`/promotions/${selectedPromo.PromotionID}`);
      setSourceCodes(data.SourceCodes || []);
      // Also refresh the global source codes list for search
      const scData = await apiRequest("/promotions/source-codes");
      setAllSourceCodes(Array.isArray(scData) ? scData : []);
    } catch (err) {
      console.error("Failed to reload source codes", err);
    }
  }, [selectedPromo]);

  const selectNew = () => {
    setSaveStatus(null);
    setScSearch("");
    setHighlightRowId(null);
    const cached = readCache();
    if (cached?.formData) {
      setFormData(cached.formData);
      setSourceCodes(cached.sourceCodes || []);
    } else {
      setFormData({ PromotionName: "", DateLaunched: "", IsActive: true, IsMembership: false });
      setSourceCodes([]);
    }
    setSelectedPromo("new");
  };

  const handleFieldChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const flashSaveStatus = (status, message = null) => {
    setSaveStatus(status);
    setSaveMessage(message);
    clearTimeout(saveStatusTimer.current);
    saveStatusTimer.current = setTimeout(() => { setSaveStatus(null); setSaveMessage(null); }, 4000);
  };

  const handleToggleSave = async (field, value) => {
    if (!selectedPromo || selectedPromo === "new") return;
    try {
      await apiRequest(`/promotions/${selectedPromo.PromotionID}`, {
        method: "PATCH",
        body: JSON.stringify({ [field]: value }),
      });
      await loadPromotions();
      flashSaveStatus("saved");
    } catch (err) {
      console.error("Toggle save failed", err);
      flashSaveStatus("error");
    }
  };

  // ── Duplicate check ─────────────────────────────────────────────────────────
  // Re-fetches active source codes fresh (never trusts stale page data) and
  // compares them against the new rows about to be saved.
  const findConflicts = async () => {
    const fresh = await apiRequest("/promotions/source-codes");
    const list  = Array.isArray(fresh) ? fresh : [];
    setAllSourceCodes(list);

    const nameById = Object.fromEntries(promotions.map(p => [p.PromotionID, p.PromotionName]));
    const existing = {};
    list.forEach(sc => {
      const c = normalizeCode(sc.SourceCode);
      if (c && !existing[c]) existing[c] = `already in ${nameById[sc.PromotionID] || `promotion ${sc.PromotionID}`}`;
    });

    const seen = new Set();
    const conflicts = [];
    let addCount = 0;
    for (const sc of sourceCodes) {
      if (!sc._isNew) continue;
      const c = normalizeCode(sc.SourceCode);
      if (!c) continue;
      if (existing[c])      conflicts.push({ SourceCode: c, where: existing[c] });
      else if (seen.has(c)) conflicts.push({ SourceCode: c, where: "repeated in this paste", repeat: true });
      else addCount++;
      seen.add(c);
    }
    return { conflicts, addCount };
  };

  // ── Save ────────────────────────────────────────────────────────────────────
  // Writes the promotion and its source codes. Codes in `skip` are not added.
  // Repeats within the same save are only added once.
  const performSave = async (skip = new Set()) => {
    setSaving(true);
    const added         = new Set();
    const serverSkipped = []; // caught by the API's 409 backstop

    const addCode = async (promotionId, sc, code) => {
      if (skip.has(code) || added.has(code)) return;
      try {
        await apiRequest(`/promotions/${promotionId}/source-codes`, {
          method: "POST",
          body: JSON.stringify({ SourceCode: code, SourceCodeDescription: sc.SourceCodeDescription || "" }),
        });
        added.add(code);
      } catch (err) {
        if (isDuplicateError(err)) { serverSkipped.push(code); return; }
        throw err;
      }
    };

    try {
      if (selectedPromo === "new") {
        const newPromo = await apiRequest("/promotions", {
          method: "POST",
          body: JSON.stringify({ PromotionName: formData.PromotionName, DateLaunched: formData.DateLaunched, IsActive: formData.IsActive !== false, IsMembership: formData.IsMembership === true }),
        });
        const newId = newPromo.PromotionID;
        for (const sc of sourceCodes) {
          const code = normalizeCode(sc.SourceCode);
          if (!code) continue;
          await addCode(newId, sc, code);
        }
        const verified = await apiRequest(`/promotions/${newId}`);
        if (verified?.PromotionID) clearCache();
        await loadPromotions();
        await selectPromotion(newPromo);
      } else {
        await apiRequest(`/promotions/${selectedPromo.PromotionID}`, {
          method: "PATCH",
          body: JSON.stringify({ PromotionName: formData.PromotionName, DateLaunched: formData.DateLaunched, IsActive: formData.IsActive !== false, IsMembership: formData.IsMembership === true }),
        });
        for (const sc of sourceCodes) {
          const code = normalizeCode(sc.SourceCode);
          if (!code) continue;
          if (sc._isNew) {
            await addCode(selectedPromo.PromotionID, sc, code);
          } else if (sc.RowID) {
            await apiRequest(`/promotions/${selectedPromo.PromotionID}/source-codes/${sc.RowID}`, {
              method: "PATCH",
              body: JSON.stringify({ SourceCode: code, SourceCodeDescription: sc.SourceCodeDescription || "" }),
            });
          }
        }
        // Reload so saved rows lose their _isNew flag. Without this, every
        // extra Save click re-POSTs the same rows and creates duplicates.
        await reloadSourceCodes();
        await loadPromotions();
      }

      if (serverSkipped.length) {
        console.warn("Skipped by server (already active elsewhere):", serverSkipped);
        flashSaveStatus("saved", `Saved. Skipped ${serverSkipped.length} already in use: ${serverSkipped.join(", ")}`);
      } else {
        flashSaveStatus("saved");
      }
    } catch (err) {
      console.error("Save failed", err);
      flashSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  // Checks for duplicates first. If any are found, shows the warning and
  // waits for the user. If the check itself fails, nothing is saved.
  const handleSave = async () => {
    setSaving(true);
    try {
      const { conflicts, addCount } = await findConflicts();
      if (conflicts.length) {
        setDupWarning({ conflicts, addCount });
        setSaving(false);
        return;
      }
    } catch (err) {
      console.error("Duplicate check failed", err);
      flashSaveStatus("error", "Couldn't check for duplicate source codes. Nothing was saved — try again.");
      setSaving(false);
      return;
    }
    await performSave();
  };

  const handleDupConfirm = () => {
    // Skip codes that exist elsewhere. Repeats within this paste are not
    // skipped here — performSave adds the first one and ignores the rest.
    const skip = new Set(dupWarning.conflicts.filter(c => !c.repeat).map(c => c.SourceCode));
    setDupWarning(null);
    performSave(skip);
  };

  // ── Search logic ────────────────────────────────────────────────────────────
  // Build a map of PromotionID → matched source code rows for the current search
  const scMatchMap = React.useMemo(() => {
    if (!search) return {};
    const term = search.toLowerCase();
    const map = {};
    allSourceCodes.forEach(sc => {
      const codeMatch = sc.SourceCode?.toLowerCase().includes(term);
      const descMatch = sc.SourceCodeDescription?.toLowerCase().includes(term);
      if (codeMatch || descMatch) {
        if (!map[sc.PromotionID]) map[sc.PromotionID] = [];
        map[sc.PromotionID].push(sc);
      }
    });
    return map;
  }, [search, allSourceCodes]);

  const allPromotionNames = promotions.map(p => ({ id: p.PromotionID, name: p.PromotionName }));

  const filteredPromotions = promotions.filter(p => {
    const matchActive = filterActive === "all" || (filterActive === "active" ? p.IsActive !== false : p.IsActive === false);
    if (!search) return matchActive;
    const matchName = p.PromotionName?.toLowerCase().includes(search.toLowerCase());
    const matchSc   = !!scMatchMap[p.PromotionID];
    return matchActive && (matchName || matchSc);
  });

  // Detect data hygiene issue: same source code in multiple promotions
  const duplicateScPromoIds = React.useMemo(() => {
    if (!search) return new Set();
    const term = search.toLowerCase();
    const exactMatches = allSourceCodes.filter(sc => sc.SourceCode?.toLowerCase() === term);
    const promoIds = new Set(exactMatches.map(sc => sc.PromotionID));
    return promoIds.size > 1 ? promoIds : new Set();
  }, [search, allSourceCodes]);

    const handlePromotionClick = (p) => {
    const term = search.toLowerCase();
    const matched = scMatchMap[p.PromotionID] || [];
    // Highlight + scroll to the first matching row, but show every code
    const firstScMatch = matched.find(sc =>
      sc.SourceCode?.toLowerCase().includes(term) ||
      sc.SourceCodeDescription?.toLowerCase().includes(term)
    );
    selectPromotion(p, firstScMatch?.RowID || null);
  };
  

  const filterBtnClass = (active) => [
    "flex-1 py-1.5 rounded-[var(--radius-md)] border text-xs font-semibold transition-colors capitalize",
    active
      ? "border-[var(--color-orange)] bg-[var(--color-orange-light)] text-[var(--color-orange)]"
      : "border-[var(--color-border)] bg-white text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]",
  ].join(" ");

  return (
    <>
      <style>{PULSE_STYLE}</style>
      <div className="h-full flex flex-col overflow-hidden bg-[var(--color-surface)]">
        <PageHeader
          title="Promotions"
          subtitle="Manage membership promotions and source codes"
          action={<Button variant="primary" onClick={selectNew} icon={<Plus size={14} />}>New Promotion</Button>}
        />

        <div className="flex-1 flex overflow-hidden min-h-0">

          {/* Left list panel */}
          <div className="w-[340px] min-w-[280px] border-r border-[var(--color-border)] flex flex-col bg-white shrink-0">
            <div className="px-3 py-3 border-b border-[var(--color-border)] space-y-2">
              <div className="relative">
                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)] pointer-events-none" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or source code…"
                  className="w-full pl-8 pr-3 h-8 rounded-[var(--radius-md)] border border-[var(--color-border)] text-xs text-[var(--color-text-primary)] placeholder-[var(--color-text-muted)] focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)] focus:border-[var(--color-orange)] bg-white"
                />
              </div>
              <div className="flex gap-1.5">
                {["active", "inactive", "all"].map(f => (
                  <button key={f} onClick={() => setFilterActive(f)} className={filterBtnClass(filterActive === f)}>
                    {f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto py-1.5 px-2">
              {loading ? (
                <div className="flex items-center gap-2 text-xs text-[var(--color-text-muted)] p-4">
                  <Spinner size="sm" /> Loading…
                </div>
              ) : filteredPromotions.length === 0 ? (
                <p className="text-xs text-[var(--color-text-muted)] p-4">No promotions found.</p>
              ) : filteredPromotions.map(p => {
                const isSelected  = selectedPromo && selectedPromo !== "new" && selectedPromo.PromotionID === p.PromotionID;
                const matchedScs  = scMatchMap[p.PromotionID] || [];
                const isDuplicate = duplicateScPromoIds.has(p.PromotionID);

                return (
                  <button
                    key={p.PromotionID}
                    onClick={() => handlePromotionClick(p)}
                    className={[
                      "w-full flex items-center justify-between px-3 py-2.5 rounded-[var(--radius-lg)] border text-left mb-0.5 transition-colors",
                      isSelected ? "border-[var(--color-orange)] bg-[var(--color-orange-light)]" : "border-transparent hover:bg-[var(--color-surface)]",
                    ].join(" ")}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs font-semibold truncate ${isSelected ? "text-[var(--color-orange)]" : "text-[var(--color-text-primary)]"}`}>
                        {p.PromotionName}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[10px] text-[var(--color-text-muted)]">{p.DateLaunched || "—"}</span>
                        {p.IsMembership && (
                          <span className="text-[10px] font-semibold text-[var(--color-info)] bg-[var(--color-info-bg)] px-1.5 py-0.5 rounded">Membership</span>
                        )}
                        {matchedScs.length > 0 && (
                          <span className="text-[10px] font-semibold text-[var(--color-orange)] bg-[var(--color-orange-light)] px-1.5 py-0.5 rounded">
                            {matchedScs[0].SourceCode}{matchedScs.length > 1 ? ` +${matchedScs.length - 1}` : ""}
                          </span>
                        )}
                        {isDuplicate && (
                          <span className="flex items-center gap-0.5 text-[10px] font-semibold text-[var(--color-warning)] bg-[var(--color-warning-bg)] px-1.5 py-0.5 rounded">
                            <AlertCircle size={10} /> Duplicate
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`w-2 h-2 rounded-full shrink-0 ml-2 ${p.IsActive !== false ? "bg-[var(--color-success)]" : "bg-[var(--color-border-strong)]"}`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right detail panel */}
          <div className="flex-1 overflow-y-auto bg-[var(--color-surface)]">
            {!selectedPromo ? (
              <div className="flex flex-col items-center justify-center h-full gap-6 p-12">
                <IllustrationNoSelection width={220} />
                <div className="text-center">
                  <p className="text-sm font-semibold text-[var(--color-text-primary)] mb-1">Select a promotion</p>
                  <p className="text-xs text-[var(--color-text-muted)]">Choose one from the list or create a new one.</p>
                </div>
              </div>
            ) : (
              <PromotionForm
                promotion={formData}
                sourceCodes={sourceCodes}
                promotions={promotions}
                onSourceCodesChange={setSourceCodes}
                onChange={handleFieldChange}
                onSave={handleSave}
                onToggleSave={handleToggleSave}
                saving={saving}
                saveStatus={saveStatus}
                saveMessage={saveMessage}
                allPromotionNames={allPromotionNames}
                onReloadSourceCodes={reloadSourceCodes}
                highlightRowId={highlightRowId}
                scSearch={scSearch}
                onScSearchChange={setScSearch}
              />
            )}
          </div>
        </div>
      </div>

      {dupWarning && (
        <DuplicateWarningModal
          conflicts={dupWarning.conflicts}
          addCount={dupWarning.addCount}
          onCancel={() => setDupWarning(null)}
          onConfirm={handleDupConfirm}
        />
      )}
    </>
  );
}